/**
 * Utilidades del módulo Meetings — formatters + label helpers + tipos.
 * Coinciden con el contrato del prototipo P3 y con el schema en Supabase.
 */
import type { DealStage, LabeledValue } from "../types";

export type MeetingSource =
  | "manual"
  | "telegram"
  | "whatsapp"
  | "email"
  | "web";

/**
 * Forma del registro de meeting tal como vive en la BD (extendido con
 * los joins de meetings_summary para evitar fetches adicionales).
 */
export interface Meeting {
  id: number | string;
  company_id: number | string | null;
  deal_id?: number | string | null;
  sales_id: number | string;
  occurred_at: string; // ISO
  title?: string | null;
  /** Lugar donde ocurrió la junta. Texto libre: "Estadio Azteca - Sala VIP",
   *  "Zoom", "WhatsApp call", "Visita en oficinas del cliente", etc. */
  location?: string | null;
  /** Rating subjetivo de la junta. 1..5 estrellas. NULL si aún no se calificó. */
  rating?: number | null;
  topics?: string | null; // multiline text "Tema 1\nTema 2"
  agreements?: string | null;
  next_steps?: string | null;
  summary?: string | null;
  transcript?: string | null;
  audio_url?: string | null;
  source: MeetingSource;
  language?: string | null;
  raw_extraction?: Record<string, unknown> | null;
  created_at?: string;
  updated_at?: string;
  // Campos extra desde meetings_summary view:
  company_name?: string | null;
  company_sector?: string | null;
  company_city?: string | null;
  company_logo?: { src?: string; title?: string } | null;
  deal_name?: string | null;
  deal_stage?: string | null;
  deal_amount?: number | null;
  sales_name?: string | null;
  sales_avatar?: { src?: string } | null;
  sales_first_name?: string | null;
  sales_last_name?: string | null;
  attendees?: MeetingAttendee[] | null;
  attendee_count?: number | null;
}

/** Asistente expandido desde meeting_attendees + contacts (view) */
export interface MeetingAttendee {
  id: number | string;
  name: string;
  role?: string | null;
  title?: string | null;
}

// --- Formatters consistentes con prototipo P3 ---

export const formatMeetingDate = (iso: string): string => {
  const d = new Date(iso);
  const months = [
    "ene",
    "feb",
    "mar",
    "abr",
    "may",
    "jun",
    "jul",
    "ago",
    "sep",
    "oct",
    "nov",
    "dic",
  ];
  return `${d.getDate()} ${months[d.getMonth()]} ${d.getFullYear()}`;
};

export const formatMeetingTime = (iso: string): string => {
  const d = new Date(iso);
  return d.toLocaleTimeString("es-MX", {
    hour: "2-digit",
    minute: "2-digit",
  });
};

export const formatMoneyMx = (n: number, currency = "MXN"): string =>
  new Intl.NumberFormat("es-MX", {
    style: "currency",
    currency,
    maximumFractionDigits: 0,
  }).format(n);

/** Genera iniciales de 2-3 letras desde el nombre completo o de la empresa */
export const initials = (name: string | null | undefined): string => {
  if (!name) return "?";
  const parts = name
    .replace(/[—–-]/g, " ")
    .split(/\s+/)
    .filter(Boolean)
    .filter((p) => !/^(de|del|la|los|las|y|e)$/i.test(p));
  return parts
    .slice(0, 2)
    .map((p) => p[0])
    .join("")
    .toUpperCase();
};

/** Convierte text multilínea en array de items (excluye líneas vacías) */
export const linesToList = (text?: string | null): string[] => {
  if (!text) return [];
  return text
    .split(/\r?\n+/)
    .map((l) => l.replace(/^[-•·]\s*/, "").trim())
    .filter(Boolean);
};

/**
 * Score visual del meeting: si no viene en raw_extraction.score, lo
 * inferimos a partir del stage del deal asociado (heurística simple para
 * que las cards muestren un valor consistente).
 */
export const meetingScore = (m: Meeting): number => {
  const fromRaw = m.raw_extraction?.score;
  if (typeof fromRaw === "number") return fromRaw;
  // Fallback heurística por id (estable, no cambia entre renders)
  const seed = String(m.id)
    .split("")
    .reduce((a, c) => (a * 31 + c.charCodeAt(0)) >>> 0, 7);
  return 50 + (seed % 50); // 50..99
};

export const sentimentLabel = (
  score: number,
): { tone: "hot" | "warm" | "cool"; label: string } => {
  if (score > 80) return { tone: "hot", label: "Caliente" };
  if (score > 60) return { tone: "warm", label: "Tibio" };
  return { tone: "cool", label: "Frío" };
};

export const sourceLabel = (s?: MeetingSource | null): string => {
  switch (s) {
    case "telegram":
      return "Telegram";
    case "whatsapp":
      return "WhatsApp";
    case "email":
      return "Correo";
    case "web":
      return "Web";
    default:
      return "Manual";
  }
};

/** Cuántos minutos:segundos representa una duración cruda en ms o "MM:SS" */
export const formatDuration = (raw?: string | number | null): string => {
  if (raw == null) return "—";
  if (typeof raw === "string") {
    // Ya viene en formato "MM:SS" o "HH:MM:SS"
    if (/^\d{1,2}:\d{2}(:\d{2})?$/.test(raw)) return raw;
  }
  const seconds = typeof raw === "number" ? Math.round(raw / 1000) : 0;
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}:${String(s).padStart(2, "0")}`;
};

/** Devuelve el sectorLabel a partir de companySectors del config */
export const findSectorLabel = (
  sectors: LabeledValue[],
  value?: string | null,
): string => sectors.find((s) => s.value === value)?.label ?? "—";

/** Devuelve el stageLabel a partir de dealStages del config */
export const findStageLabel = (
  stages: DealStage[],
  value?: string | null,
): string => stages.find((s) => s.value === value)?.label ?? value ?? "—";
